import styled from 'styled-components';
import React, { useState} from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { addSlide, editSlide } from '@services/slides';
import { AllSlides, BackToLoginLink } from '@components';
import { generateSlideImageUrl } from '@utils/generateAssetLinks';
import { QUERY_KEYS } from '@constants';
import { getErrorMessage } from '@utils/errorHandling';
import Modal from 'react-modal';

const PageStyle = styled.div`
    display: flex;
    justify-content: space-around;
    align-items: flex-start;
    width: 100%;
`;

const Form = styled.form`
    h4, >div {
        margin-bottom: 10px;
    }
    div {
        display: flex;
        flex-direction: column;
    }
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 200px;
    #message {
        margin-top: 15px;
        border: 1px solid grey;
        border-radius: 5px;
        padding: 5px;
    }
`;

const Reference = styled.div`
    display: inherit;
    flex-direction: column;
    position: absolute;
    left: 2rem;
    position: relative;
    flex-direction: column;
    right: 2rem;
    width: 350px;
`;

const SlideDetailStyle = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-top: 10px;
`;

const modalStyle = {
    content: {
      height: '40%',
      width: '25%',
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column'
    },
};

const NewSlideInput = ({ name, value, handleChange }) => (
    <div>
        <label htmlFor={name.toUpperCase()}>
            <strong>{name}</strong>
        </label>
        <input 
            placeholder={value ? value.toString() : 'null'} 
            value={value === null ? 'null' : value} 
            name={name} 
            id={name.toUpperCase()} 
            onChange={handleChange} />
    </div>
);

const SlideDetail = ({ name, value }) => (
    <SlideDetailStyle>
        <label htmlFor={name}>
            <strong>{name}</strong>
        </label>
        <span id={name.toUpperCase()}>
            {value ? value.toString() : 'null'}
        </span>
    </SlideDetailStyle>
);

export const AddSlidePage = () => {
    const queryClient = useQueryClient();
    const { allSlidesQuery } = QUERY_KEYS;
    const [slideId, setSlideId] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [message, setMessage] = useState('');
    const defaultData = {
        name: '',
        productType: 'Debt Settlement',
        profile: 'Strategic Consulting',
        isRequired: false,
        videoUrl: '',
        tags: ''
    };

    const [slideData, setSlideData] = useState(defaultData);

    const addSlideMutation = useMutation(slideData => addSlide(slideData), {
        onSuccess: slide => {
            setMessage(`Your slide was created successfully: ${JSON.stringify(slide.data)}`);
            queryClient.invalidateQueries(allSlidesQuery);
        },
        onError: err => {
            setMessage(`Error creating new slide: ${getErrorMessage(err)}`);
        }
    });

    const editSlideMutation = useMutation(slideData => editSlide(slideData), {
        onSuccess: () => {
            setMessage(`Slide '${slideData.name}' was successfully updated`);
            queryClient.invalidateQueries(allSlidesQuery);
        },
        onError: err => {
            setMessage(`Error editing slide: ${getErrorMessage(err)}`);
        }
    });

    const handleChange = (event) => {
        const { name, value } = event.target;

        setSlideData(prevSlideData => ({...prevSlideData, [name]: value }));
    };

    const handleSelectSlide = (slide) => {
        const { id, createdAt, updatedAt, ...relevantFields } = slide;

        setEditMode(true);
        setSlideId(id);
        setSlideData({ ...relevantFields });
    };

    const resetToDefault = (event) => {
        event.preventDefault();

        setEditMode(false);
        setSlideData({...defaultData});
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (!slideData.name) {
            setMessage('The `name` field is required');
        } else {
            openModal();
        }
    };

    function confirmSlideDetails() {
        closeModal();

        const dataToUpload = {...slideData, id: slideId };
        // We need the initial data to be empty strings for React controlled components
        // to work, but we want to make sure empty values are null upon adding/updating to DB.
        for (let key in dataToUpload) {
            if (key in dataToUpload && dataToUpload[key] === '') {
                dataToUpload[key] = null;
            }
        }

        if (editMode) {
            editSlideMutation.mutate(dataToUpload);
        } else {
            const { id, ...fields } = dataToUpload;
            addSlideMutation.mutate(fields);
        }
    }

    function openModal() {
        setModalOpen(true);
    }

    function closeModal() {
        setModalOpen(false);
    }

    const slideObjectAsArray = Object.entries(slideData);

    // In the future may add more fields, but only these are necessary at the moment
    // To-do: add editing ability
    return (
        <PageStyle>
            <BackToLoginLink />
            <Modal 
                ariaHideApp={false}
                style={modalStyle}
                onRequestClose={closeModal}
                isOpen={modalOpen}>
                    <h4>CONFIRM SLIDE DETAILS</h4>
                        {
                           slideObjectAsArray.map(data => (
                                <SlideDetail 
                                    key={data[0]} 
                                    name={data[0]} 
                                    value={data[1]} />
                            ))
                        }
                    <div style={{ marginTop: '10px' }}>
                        <button onClick={closeModal}>Edit</button>
                        <button onClick={confirmSlideDetails}>Confirm</button>
                    </div>
            </Modal>
            <div>
                <h4>Click on a slide to edit</h4>
                <AllSlides handleClick={handleSelectSlide} />
            </div>
            <Form>
                <h4>{ editMode ? `Editing slide: ${slideData.name}` : 'Add new slide:'}</h4>
                <div >
                    {
                        editMode && <button onClick={resetToDefault}>Switch to new slide</button>
                    }
                </div>
                {
                    slideObjectAsArray.map(data => (
                        <NewSlideInput 
                            key={data[0]} 
                            name={data[0]} 
                            value={data[1]} 
                            handleChange={handleChange} />
                    ))
                }
                <button type='submit' onClick={handleSubmit}>Submit</button>
                {
                    message && <div id='message'>{message}</div>
                }
            </Form>
            <Reference>
                <label htmlFor='slide-model'>
                    <h4>The slide model (for reference)</h4>
                    <br/>
                    <span><em>*Inputs are prepopulated with default values</em></span>
                </label>
                <img id='slide-model' src={generateSlideImageUrl('slide-model.png')} alt='slide-model' />
            </Reference>
        </PageStyle>
    );
};

export default AddSlidePage;
